<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Input, Label, useToast } from '@manta/ui-kit';
import { ArrowLeft, Loader2, Plus, X } from 'lucide-vue-next';
import SpeciesSelect from '@/components/SpeciesSelect.vue';
import {
  useClientCreateWithPatients,
  type CreateClientWithPatientsResult,
} from '@/api/clients/useClientCreateWithPatients';
import {
  useClientUpdate,
  type UpdateClientPayload,
} from '@/api/clients/useClientUpdate';
import { clientNameParts, splitFullName } from '@/lib/clients';
import type { SpeciesKey } from '@/lib/species';
import type { MantaClient } from '@/types';

const props = defineProps<{
  mode: 'create' | 'edit';
  /** The client being edited (edit mode). */
  client?: MantaClient | null;
  /** Typed search text used to prefill the name (create mode). */
  initialName?: string;
}>();

const emit = defineEmits<{
  back: [];
  created: [result: CreateClientWithPatientsResult];
  saved: [client: MantaClient];
}>();

const { t } = useI18n();
const { toast } = useToast();
const createClient = useClientCreateWithPatients();
const updateClient = useClientUpdate();

type AnimalRow = { name: string; species: SpeciesKey | '' };
const emptyRow = (): AnimalRow => ({ name: '', species: '' });

const initial =
  props.mode === 'edit' && props.client
    ? {
        ...clientNameParts(props.client),
        phone: props.client.phone_number?.trim() ?? '',
        email: props.client.email?.trim() ?? '',
      }
    : { ...splitFullName(props.initialName ?? ''), phone: '', email: '' };

const firstName = ref(initial.firstName);
const lastName = ref(initial.lastName);
const phone = ref(initial.phone);
const email = ref(initial.email);
const animals = ref<AnimalRow[]>([emptyRow()]);
const submitted = ref(false);

const pending = computed(
  () => createClient.isPending.value || updateClient.isPending.value,
);
const emailInvalid = computed(
  () => !!email.value.trim() && !/^\S+@\S+\.\S+$/.test(email.value.trim()),
);
const lastNameMissing = computed(() => !lastName.value.trim());
const canSubmit = computed(
  () => !lastNameMissing.value && !emailInvalid.value && !pending.value,
);

function updateRow(index: number, patch: Partial<AnimalRow>) {
  animals.value = animals.value.map((row, i) =>
    i === index ? { ...row, ...patch } : row,
  );
}

function removeRow(index: number) {
  const next = animals.value.filter((_, i) => i !== index);
  animals.value = next.length ? next : [emptyRow()];
}

function changedFields(): UpdateClientPayload {
  const next = {
    firstName: firstName.value.trim(),
    lastName: lastName.value.trim(),
    phone: phone.value.trim(),
    email: email.value.trim(),
  };
  return {
    ...(next.firstName !== initial.firstName
      ? { first_name: next.firstName || null }
      : {}),
    ...(next.lastName !== initial.lastName ? { last_name: next.lastName } : {}),
    ...(next.phone !== initial.phone
      ? { phone_number: next.phone || null }
      : {}),
    ...(next.email !== initial.email ? { email: next.email || null } : {}),
  };
}

async function submit() {
  submitted.value = true;
  if (!canSubmit.value) return;
  if (props.mode === 'edit' && props.client) {
    const data = changedFields();
    if (!Object.keys(data).length) {
      emit('back');
      return;
    }
    try {
      emit(
        'saved',
        await updateClient.mutateAsync({ id: props.client.id, data }),
      );
    } catch {
      toast({
        title: t('workspace.saveCustomerError'),
        variant: 'destructive',
      });
    }
    return;
  }
  try {
    const result = await createClient.mutateAsync({
      first_name: firstName.value.trim() || undefined,
      last_name: lastName.value.trim(),
      phone_number: phone.value.trim() || undefined,
      email: email.value.trim() || undefined,
      patients: animals.value
        .map((row) => ({
          name: row.name.trim(),
          species: row.species || undefined,
        }))
        .filter((row) => row.name),
    });
    emit('created', result);
  } catch {
    toast({
      title: t('workspace.createCustomerError'),
      variant: 'destructive',
    });
  }
}
</script>

<template>
  <form class="contents" novalidate @submit.prevent="submit">
    <div class="flex items-center gap-1 border-b px-2 py-1.5">
      <button
        type="button"
        class="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        :aria-label="t('common.back')"
        @click="emit('back')"
      >
        <ArrowLeft class="size-4" />
      </button>
      <span class="text-sm font-medium">{{
        mode === 'edit'
          ? t('workspace.editCustomer')
          : t('workspace.createCustomer')
      }}</span>
    </div>

    <div class="max-h-[420px] space-y-3 overflow-y-auto p-3">
      <div class="grid grid-cols-2 gap-2">
        <div class="space-y-1.5">
          <Label for="client-first-name" class="text-xs">{{
            t('workspace.firstName')
          }}</Label>
          <Input
            id="client-first-name"
            v-model="firstName"
            data-autofocus
            class="h-9"
            autocomplete="off"
            data-1p-ignore
            data-lpignore="true"
          />
        </div>
        <div class="space-y-1.5">
          <Label for="client-last-name" class="text-xs">
            {{ t('workspace.lastName') }}
            <span class="text-destructive">*</span>
          </Label>
          <Input
            id="client-last-name"
            v-model="lastName"
            class="h-9"
            autocomplete="off"
            data-1p-ignore
            data-lpignore="true"
            :aria-invalid="submitted && lastNameMissing"
            :class="
              submitted && lastNameMissing
                ? 'border-destructive focus-visible:ring-destructive'
                : ''
            "
          />
        </div>
      </div>

      <div class="space-y-1.5">
        <Label for="client-phone" class="text-xs">{{
          t('workspace.customerPhone')
        }}</Label>
        <Input
          id="client-phone"
          v-model="phone"
          type="tel"
          class="h-9"
          autocomplete="off"
          data-1p-ignore
          data-lpignore="true"
        />
      </div>

      <div class="space-y-1.5">
        <Label for="client-email" class="text-xs">{{
          t('workspace.customerEmail')
        }}</Label>
        <Input
          id="client-email"
          v-model="email"
          type="email"
          class="h-9"
          autocomplete="off"
          data-1p-ignore
          data-lpignore="true"
          :aria-invalid="emailInvalid"
          :class="
            submitted && emailInvalid
              ? 'border-destructive focus-visible:ring-destructive'
              : ''
          "
        />
        <p v-if="submitted && emailInvalid" class="text-xs text-destructive">
          {{ t('workspace.invalidEmail') }}
        </p>
      </div>

      <div v-if="mode === 'create'" class="space-y-1.5 pt-1">
        <p class="text-xs font-medium">{{ t('workspace.animalsLabel') }}</p>
        <div
          v-for="(row, index) in animals"
          :key="index"
          class="flex items-center gap-1"
        >
          <Input
            :model-value="row.name"
            :placeholder="t('workspace.patientForm.name')"
            class="h-8 min-w-0 flex-1"
            @update:model-value="updateRow(index, { name: String($event) })"
          />
          <SpeciesSelect
            :model-value="row.species"
            @update:model-value="updateRow(index, { species: $event })"
          />
          <button
            type="button"
            class="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:invisible"
            :disabled="animals.length === 1 && !row.name && !row.species"
            :aria-label="t('workspace.removeAnimal')"
            @click="removeRow(index)"
          >
            <X class="size-4" />
          </button>
        </div>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-md px-1 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          @click="animals = [...animals, emptyRow()]"
        >
          <Plus class="size-3.5" />
          {{ t('workspace.addAnotherAnimal') }}
        </button>
      </div>
    </div>

    <div class="flex justify-end gap-2 border-t p-2">
      <Button type="button" variant="ghost" size="sm" @click="emit('back')">
        {{ t('common.cancel') }}
      </Button>
      <Button
        type="submit"
        size="sm"
        :disabled="pending || (submitted && !canSubmit)"
      >
        <Loader2 v-if="pending" class="mr-1 size-3.5 animate-spin" />
        {{ mode === 'edit' ? t('common.save') : t('workspace.createAndUse') }}
      </Button>
    </div>
  </form>
</template>
